/**
 * defa.views.PhoneNumberContainer
 * Base Container for all phone number views.
 * @version 1.2.1
 */
import PhoneNumberCollectionView from 'scripts/defa/phone-numbers/view-phone-number-collection-view';
import PhoneNumberCreateForm     from 'scripts/defa/phone-numbers/view-phone-number-create-form';
import PhoneNumberDeleteForm     from 'scripts/defa/phone-numbers/view-phone-number-delete-form';
import PhoneNumberUpdateForm     from 'scripts/defa/phone-numbers/view-phone-number-update-form';
import ActiveContainer           from 'scripts/poppet/active-container';
import template                  from 'templates/phoneNumberContainer.jst.html';


const channelLayout = Bb.Radio.channel( 'layout' ),
	channelNav    = Bb.Radio.channel( 'nav' );

const selectors            = {
	regionContainer: '[data-region=phone-container]',
	buttonCreate   : '[data-click=phone-create]',
	buttonCancel   : '[data-click=phone-cancel]',
};
const PhoneNumberContainer = ActiveContainer.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	ui: {
		cancel: selectors.buttonCancel,
		create: selectors.buttonCreate,
	},
	
	regions: {
		container: selectors.regionContainer,
	},
	
	events: {
		'click @ui.create': 'onClickCreate',
	},
	
	templateContext: function () {
		return {
			email: this.options.currentUser.get( 'email' ),
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// @ui event handlers
	
	/**
	 * Triggers when a user clicks the @ui.create button.
	 * @void
	 */
	onClickCreate: function ( event ) {
		event.preventDefault();
		channelNav.trigger( 'phone:create', [ this.options.currentUser.get( 'id' ) ] );
	},
	
	// snippet child view event handlers
	
	/**
	 * Triggers when the child view triggers a "delete" event.
	 * @void
	 */
	onChildviewDelete: function ( id ) {
		channelNav.trigger( 'phone:delete', [ this.options.currentUser.get( 'id' ), id ] );
	},
	
	/**
	 * Triggers when the child view triggers an "open" event.
	 * @void
	 */
	onChildviewOpen: function ( childView ) {
		channelNav.trigger( 'phone:update', [ this.options.currentUser.get( 'id' ), childView.model.get( 'id' ), ] );
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// container functions
	
	/**
	 * Attaches the correct view based on this PhoneNumberContainer instance's options.
	 * @void
	 */
	attachView: function () {
		
		switch ( this.options.phoneView ) {
			
			case 'phone:create' :
				this._hideCancelButton();
				this._hideCreateButton();
				this._showCreateView( this.options );
				break;
			
			case 'phone:list' :
				this._showCancelButton();
				this._showCreateButton();
				this._showListView( this.options );
				break;
			
			case 'phone:update' :
				this._hideCancelButton();
				this._showCreateButton();
				this._showUpdateView( this.options );
				break;
			
			case 'phone:delete' :
				this._hideCancelButton();
				this._hideCreateButton();
				this._showDeleteView( this.options );
				break;
			
			default :
				channelLayout.trigger( 'error', { model: this.options.model } );
				break;
		}
	},
	
	/**
	 * Checks if the current set of options matches the current phoneNumbers collection.
	 * @return {bool} True if all requirements pass, false if not.
	 */
	checkRequirements: function () {
		// do a quick check for view requirements.
		switch ( this.options.phoneView ) {
			
			case 'phone:list'   :
			case 'phone:update' :
			case 'phone:delete' :
				if ( 0 === this.options.phoneNumbers.length ) {
					channelNav.trigger( 'phone:create', [ this.options.currentUser.get( 'id' ), ], { trigger: true, replace: true, } );
					return false;
				}
				break;
		}
		return true;
	},
	
	// @ui functions
	
	/**
	 * Hides the 'create' button.
	 * @void
	 */
	_hideCancelButton: function () {
		this.ui.cancel.hide();
	},
	
	/**
	 * Shows the 'create' button.
	 * @void
	 */
	_showCancelButton: function () {
		this.ui.cancel.show();
	},
	
	/**
	 * Hides the 'create' button.
	 * @void
	 */
	_hideCreateButton: function () {
		this.ui.create.hide();
	},
	
	/**
	 * Shows the 'create' button.
	 * @void
	 */
	_showCreateButton: function () {
		this.ui.create.show();
	},
	
	// view functions
	
	/**
	 * Displays the create PhoneNumber view.
	 * @param {Object} options  The UserController options object, containing all needed information.
	 * @void
	 */
	_showCreateView: function () {
		let phoneNumberCreateForm = new PhoneNumberCreateForm( _.extend( this.options, {
			model: this.options.phoneNumber,
		} ) );
		
		this.showChildView( 'container', phoneNumberCreateForm );
	},
	
	/**
	 * Displays the list PhoneNumbers view.
	 * @param {Object} options  The UserController options object, containing all needed information.
	 * @void
	 */
	_showListView: function () {
		let phoneNumberCollectionView = new PhoneNumberCollectionView( _.extend( this.options, {
			collection: this.options.phoneNumbers,
		} ) );
		
		this.showChildView( 'container', phoneNumberCollectionView );
	},
	
	/**
	 * Displays the update PhoneNumbers view.
	 * @param {Object} options  The UserController options object, containing all needed information.
	 * @void
	 */
	_showUpdateView: function () {
		
		let phoneNumberUpdateForm = new PhoneNumberUpdateForm( _.extend( this.options, {
			model: this.options.phoneNumber,
		} ) );
		
		this.showChildView( 'container', phoneNumberUpdateForm );
	},
	
	/**
	 * Displays the delete PhoneNumbers view.
	 * @param {Object} options  The UserController options object, containing all needed information.
	 * @void
	 */
	_showDeleteView: function () {
		let phoneNumberDeleteForm = new PhoneNumberDeleteForm( _.extend( this.options, {
			model: this.options.phoneNumber,
		} ) );
		
		this.showChildView( 'container', phoneNumberDeleteForm );
	},
	
} );

export default PhoneNumberContainer;